import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Building2, Target, Users, BarChart3, ArrowRight, CheckCircle } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import CaseStudies, { abmReviews } from "@/components/CaseStudies";

const AccountBasedMarketing = () => {
  const benefits = [
    "Target accounts that match your best customers",
    "Personalized messaging for each buying committee",
    "Multi-threaded engagement across stakeholders",
    "Intent signals to prioritize hot accounts",
    "Sales and marketing on the same target list",
    "ROI measured by account penetration and pipeline",
  ];

  const process = [
    {
      icon: Building2,
      title: "Account Selection",
      description: "Identify and tier target accounts based on fit, intent, and propensity to buy.",
    },
    {
      icon: Users,
      title: "Buying Committee Mapping",
      description: "Map key stakeholders and decision-makers within each target account.",
    },
    {
      icon: Target,
      title: "Personalized Campaigns",
      description: "Deploy account-specific content and ads that resonate with each persona.",
    },
    {
      icon: BarChart3,
      title: "Measure & Optimize",
      description: "Track account engagement, pipeline velocity, and deal progression.",
    },
  ];

  return (
    <>
      <EnhancedSEO
        type="service"
        title="Account-Based Marketing (ABM) | VGP - Enterprise B2B Growth"
        description="Land and expand your highest-value accounts with strategic ABM. Personalized campaigns, buying committee engagement, and measurable pipeline impact."
        keywords="ABM, account-based marketing, enterprise sales, B2B marketing, target accounts"
        url="/abm"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-background to-primary/5" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 bg-secondary/10 text-secondary text-sm font-medium rounded-full mb-6">
                Account-Based Marketing
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Win the Accounts That <span className="text-primary">Matter Most</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Stop spraying and praying. Focus your resources on accounts that fit your ICP and show real buying intent.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="premium" asChild>
                  <Link to="/do-you-qualify">
                    Build Your ABM Strategy
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">See Investment</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                What You Get
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Our ABM Process
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                A strategic approach to landing and expanding your highest-value accounts.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                    <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-7 w-7 text-secondary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">3x</div>
                  <p className="text-muted-foreground">Higher close rates vs. traditional marketing</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">40%</div>
                  <p className="text-muted-foreground">Larger average deal sizes</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">91%</div>
                  <p className="text-muted-foreground">Of marketers say ABM delivers higher ROI</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <CaseStudies 
          title="ABM Success Stories"
          subtitle="See how we've helped B2B companies land and expand high-value accounts."
          reviews={abmReviews}
        />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-secondary/10 via-background to-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Land Your Dream Accounts?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Let us build an ABM strategy tailored to your target market and sales motion.
              </p>
              <Button size="lg" variant="premium" asChild>
                <Link to="/do-you-qualify">
                  Schedule Strategic Review
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AccountBasedMarketing;